import React, { useState } from 'react'
import { Button, Dimmer, Form, Grid, Header, Icon, Loader, Message, Modal } from 'semantic-ui-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PdfPreviewModal } from './PdfPreviewModal'
import { buildFileUrl, getDisplayFileName, shouldPreviewAsPdf } from '../../utils/fileUtils'

const TIPOS_COMPROBANTE = ['A', 'B', 'C', 'E', 'M']

const VALORES_VACIOS = {
  numero_factura: '',
  tipo_comprobante: '',
  moneda: 'ARS',
  emisor_cuit: '',
  emisor_razon_social: '',
  receptor_cuit: '',
  receptor_razon_social: '',
  fecha_emision: '',
  importe_neto: '',
  importe_iva: '',
  importe_total: ''
}

const normalizarValores = (datos) => {
  if (!datos) return { ...VALORES_VACIOS }

  return Object.keys(VALORES_VACIOS).reduce((acumulado, campo) => {
    const valor = datos[campo]
    acumulado[campo] = valor === null || valor === undefined ? VALORES_VACIOS[campo] : String(valor)
    return acumulado
  }, {})
}

const validationSchema = Yup.object({
  importe_total: Yup.number().required(true).typeError(true)
})

/**
 * Muestra los datos que extrajo la IA de una factura para que el usuario los revise,
 * corrija y confirme. Tambien se usa para editar una factura ya guardada.
 */
export const FacturaAnalisisModal = ({
  open,
  cargando = false,
  urlArchivo,
  datos,
  error,
  aseguradoraNombre,
  clienteNombre,
  modoEdicion = false,
  guardando = false,
  onConfirm,
  onClose
}) => {
  const [previewAbierto, setPreviewAbierto] = useState(false)

  const formik = useFormik({
    initialValues: normalizarValores(datos),
    validationSchema,
    enableReinitialize: true,
    onSubmit: (valores) => {
      // El backend espera null en vez de string vacio para fechas e importes
      const limpio = Object.entries(valores).reduce((acumulado, [campo, valor]) => {
        acumulado[campo] = valor === '' ? null : valor
        return acumulado
      }, {})
      onConfirm(limpio)
    }
  })

  const campoTexto = (name, label, extra = {}) => (
    <Form.Input
      fluid
      label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      disabled={cargando || guardando}
      {...extra}
    />
  )

  const esPdf = shouldPreviewAsPdf(urlArchivo)

  return (
    <>
      <Modal open={open} onClose={onClose} size="large" closeIcon={!cargando && !guardando}>
        <Modal.Header>
          {modoEdicion ? 'Editar factura' : 'Revisar datos de la factura'}
        </Modal.Header>
        <Modal.Content scrolling>
          <Dimmer active={cargando} inverted>
            <Loader>Analizando la factura con IA...</Loader>
          </Dimmer>

          {!cargando && (
            <>
              {error && (
                <Message warning>
                  <Message.Header>No se pudieron extraer los datos automaticamente</Message.Header>
                  <p>{error}</p>
                  <p>Podes cargar los datos a mano y confirmar igual.</p>
                </Message>
              )}

              <Grid stackable columns={2} style={{ marginBottom: '0.5em' }}>
                <Grid.Column>
                  <Header sub>Aseguradora</Header>
                  <span>{aseguradoraNombre || '-'}</span>
                </Grid.Column>
                <Grid.Column>
                  <Header sub>Cliente / Beneficiario</Header>
                  <span>{clienteNombre || '-'}</span>
                </Grid.Column>
              </Grid>

              {urlArchivo && (
                <p>
                  <Icon name="file outline" />
                  {getDisplayFileName(urlArchivo)}
                  {esPdf ? (
                    <Button
                      size="mini"
                      basic
                      style={{ marginLeft: '1em' }}
                      onClick={() => setPreviewAbierto(true)}
                      type="button"
                    >
                      Ver archivo
                    </Button>
                  ) : (
                    <Button
                      size="mini"
                      basic
                      as="a"
                      href={buildFileUrl(urlArchivo)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginLeft: '1em' }}
                      type="button"
                    >
                      Ver archivo
                    </Button>
                  )}
                </p>
              )}

              <Form onSubmit={formik.handleSubmit}>
                <Form.Group widths="equal">
                  {campoTexto('numero_factura', 'N° de factura')}
                  <Form.Select
                    fluid
                    label="Tipo"
                    options={TIPOS_COMPROBANTE.map((t) => ({ key: t, text: t, value: t }))}
                    value={formik.values.tipo_comprobante}
                    onChange={(_, data) => formik.setFieldValue('tipo_comprobante', data.value)}
                    disabled={guardando}
                    clearable
                  />
                  {campoTexto('moneda', 'Moneda')}
                </Form.Group>

                <Form.Group widths="equal">
                  {campoTexto('fecha_emision', 'Fecha de emision', { type: 'date' })}
                </Form.Group>

                <Form.Group widths="equal">
                  {campoTexto('emisor_cuit', 'CUIT emisor')}
                  {campoTexto('emisor_razon_social', 'Razon social emisor')}
                </Form.Group>

                <Form.Group widths="equal">
                  {campoTexto('receptor_cuit', 'CUIT receptor')}
                  {campoTexto('receptor_razon_social', 'Razon social receptor')}
                </Form.Group>

                <Form.Group widths="equal">
                  {campoTexto('importe_neto', 'Neto', { type: 'number', step: '0.01' })}
                  {campoTexto('importe_iva', 'IVA', { type: 'number', step: '0.01' })}
                  {campoTexto('importe_total', 'Total *', { type: 'number', step: '0.01' })}
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} disabled={cargando || guardando}>
            Cancelar
          </Button>
          <Button
            primary
            onClick={formik.handleSubmit}
            disabled={cargando || guardando}
            loading={guardando}
          >
            {modoEdicion ? 'Guardar cambios' : 'Confirmar y guardar'}
          </Button>
        </Modal.Actions>
      </Modal>

      <PdfPreviewModal
        open={previewAbierto}
        onClose={() => setPreviewAbierto(false)}
        filePath={urlArchivo}
      />
    </>
  )
}
